import { useArtifactDetail } from "@/api/artifacts";
import { MoleculeViewerPanel } from "@/components/viz/MoleculeViewerPanel";

export interface SelectedInputPreviewProps {
  /** `port_name → content_hash`, exactly the picker's `selected` map. */
  selected: Record<string, string>;
}

/**
 * Previews the molecule a user has chosen for an input port, before they
 * submit anything (flowstack#950 Part A).
 *
 * **It renders what the CATALOG stored, never the file the browser holds.**
 * That is the whole point and it is enforced structurally rather than by
 * discipline: this component is given a `content_hash` and nothing else, so
 * the only thing it *can* fetch is the stored artifact. There is no code path
 * from a `File` object to this panel, including on the upload route — the
 * uploader posts to `/api/molecules`, the server reads, repairs and
 * catalogues the SDF, and the hash of that stored row is what arrives here.
 *
 * The distinction is the feature. `read_and_repair` may adjust protonation,
 * infer bond orders or reject the input outright; previewing the bytes the
 * user already has on disk would show them their own file back and hide the
 * standardisation change a preview exists to reveal.
 *
 * **Type-addressed, so there is no per-port or per-tile mapping** (D70 /
 * VISUALIZATION_PLAN C2). This component does not know or care which port
 * types are "molecule ports": it hands every selection to
 * `MoleculeViewerPanel`, which decides from the fetched artifact's
 * `typed_object`. A port declared `ForceFieldBundle` renders nothing because
 * the panel returns `null` for a type that carries no structure — not because
 * anything here filtered it out. A new structure-bearing typed object becomes
 * previewable by being added to `STRUCTURE_BEARING_TYPES`, with no edit here.
 *
 * Nothing at all renders when no selection is viewable, so a tile with no
 * molecule inputs gains no empty box.
 */
export function SelectedInputPreview({ selected }: SelectedInputPreviewProps): JSX.Element | null {
  const entries = Object.entries(selected).filter(([, hash]) => hash !== "");
  if (entries.length === 0) return null;

  return (
    <div className="mt-4 space-y-4">
      {entries.map(([portName, contentHash]) => (
        // One child component per port so each may run its own
        // `useArtifactDetail` — hooks cannot be called in a loop, and the
        // ports a user has filled in change between renders.
        <PortPreview key={portName} portName={portName} contentHash={contentHash} />
      ))}
    </div>
  );
}

function PortPreview({
  portName,
  contentHash,
}: {
  portName: string;
  contentHash: string;
}): JSX.Element | null {
  const { data: artifact } = useArtifactDetail(contentHash);

  // No placeholder while loading, and none on error. A submission form is
  // not the place to explain a failed detail fetch: the preview is an aid,
  // the select above it is the source of truth for what will be submitted,
  // and a skeleton or an error box on every non-molecule port would be the
  // "empty viewer" the brief rules out. The panel itself is loud about
  // failures once there IS a structure-bearing artifact to be loud about.
  if (artifact === undefined) return null;

  return <MoleculeViewerPanel artifact={artifact} heading={`Preview — ${portName}`} />;
}
